import { queryClient } from '@/queryClient';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import {
    NotificationContent,
    NotificationType,
} from '../constants/notification';
import { notificationService } from './notificationServices';

// 무한스크롤 알림 조회 훅
export function useNotificationInfiniteQuery(userId: string) {
    return useInfiniteQuery({
        initialPageParam: 1,
        queryKey: ['notifications', userId],
        queryFn: ({ pageParam }) =>
            notificationService.getPaginatedNotifications(
                userId,
                pageParam,
                10
            ),
        getNextPageParam: (lastPage, allPages) =>
            lastPage.length === 10 ? allPages.length + 1 : undefined,
        enabled: !!userId,
    });
}

// 읽음 처리 훅
export function useUpdateNotificationReadStatus() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            notificationId,
            isRead,
            isViewed = true,
        }: {
            notificationId: string;
            isRead: boolean;
            isViewed?: boolean;
        }) =>
            notificationService.updateNotificationReadStatus(
                notificationId,
                isRead,
                isViewed
            ),
        onSuccess: async () => {
            // 관련된 모든 쿼리 무효화
            await queryClient.invalidateQueries({
                queryKey: ['notifications'],
            });

            const userId = useAuthStore.getState().user?.id;
            if (userId) {
                void useNotificationStore.getState().updateUnreadCount(userId);
            }
        },
        onError: error => {
            console.error('Failed to update notification read status:', error);
        },
    });
}

// 모두 읽음 처리 훅
export function useMarkAllAsRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userId }: { userId: string }) =>
            notificationService.markAllAsRead(userId),
        onSuccess: async () => {
            // 관련된 모든 쿼리 무효화
            await queryClient.invalidateQueries({
                queryKey: ['notifications'],
            });
        },
        onError: error => {
            console.error('Failed to mark all notifications as read:', error);
        },
    });
}

// 조회 완료 처리 훅
export function useMarkAllAsViewed() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userId }: { userId: string }) =>
            notificationService.markAllAsViewed(userId),
        onSuccess: async () => {
            // 관련된 모든 쿼리 무효화
            await queryClient.invalidateQueries({
                queryKey: ['notifications'],
            });
        },
        onError: error => {
            console.error('Failed to mark all notifications as viewed:', error);
        },
    });
}

// 알림 추가 훅
export function useCreateNotification() {
    return useMutation({
        mutationFn: ({
            userId,
            type,
            content,
            relatedId,
        }: {
            userId: string;
            type: NotificationType;
            content: NotificationContent;
            relatedId: string;
        }) => {
            if (!userId) {
                console.warn('사용자가 존재하지 않아 알림 발송을 건너뜁니다.');
                return;
            }

            return notificationService.createTypedNotification(
                userId,
                type,
                content,
                relatedId
            );
        },
        onError: error => {
            console.error('Failed to create notification:', error);
        },
    });
}

// 알림 삭제 훅
export function useDeleteNotification() {
    return useMutation({
        mutationFn: ({ notificationId }: { notificationId: string }) =>
            notificationService.deleteNotificationWithId(notificationId),
        onError: error => {
            console.error('Failed to delete notification:', error);
        },
    });
}

export function useDeleteNotificationWithRelatedData() {
    return useMutation({
        mutationFn: ({
            userId,
            relatedId,
            type,
            registerId,
        }: {
            userId: string;
            relatedId: string;
            type: NotificationType;
            registerId?: string;
        }) =>
            notificationService.deleteNotificationWithRelatedData({
                userId,
                relatedId,
                type,
                registerId: registerId,
            }),

        onError: error => {
            console.error(
                'Failed to delete notification with related data:',
                error
            );
        },
    });
}

// 스케줄 삭제 훅
export function useDeleteSchedule() {
    return useMutation({
        mutationFn: ({
            relatedType,
            relatedId,
            notificationType,
        }: {
            relatedType: 'goal' | 'project' | 'todo';
            relatedId: string;
            notificationType: NotificationType;
        }) =>
            notificationService.deleteSchedule({
                relatedType,
                relatedId,
                notificationType,
            }),
        onError: error => {
            console.error('Failed to delete schedule:', error);
        },
    });
}

// 계획 시간 알림 설정 조회 훅
export function useGetPlanTimeSetting(userId: string) {
    return useQuery({
        queryKey: ['userNotification', userId],
        queryFn: () => notificationService.getPlanTimeSetting(userId),
        enabled: !!userId,
    });
}

// 계획 시간 알림 설정 upsert 훅
export function useUpsertPlanTimeSetting() {
    const user = useAuthStore.getState().user;

    return useMutation({
        mutationFn: ({
            userId,
            time,
            weekday,
        }: {
            userId: string;
            time: string;
            weekday: number[];
        }) =>
            notificationService.upsertPlanTimeSetting({
                userId,
                time,
                weekday,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['userNotification', user?.id],
            });
        },
        onError: error => {
            console.error('Failed to upsert plan time setting:', error);
        },
    });
}

// 계획 시간 알림 설정 비활성화 훅
export function useDisablePlanTimeSetting() {
    const user = useAuthStore.getState().user;

    return useMutation({
        mutationFn: ({
            userId,
        }: {
            userId: string;
            // fields: Partial<UserNotificationRow>;
        }) =>
            notificationService.disablePlanTimeSetting({
                userId,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['userNotification', user?.id],
            });
        },
        onError: error => {
            console.error('Failed to disable plan time setting:', error);
        },
    });
}
